import React, { FC, useState, useEffect } from 'react';
import { Form, FormFeedback, FormGroup, Input, Label } from 'reactstrap';
import Select, { SingleValue, OptionsOrGroups, GroupBase } from 'react-select';
import { Button, ReactSelectOverrides } from '@bphxd/ds-core-react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { EMPTY_JOB } from '../../../lib/constants';
import { TJob } from '../../../lib/types';
import { useSelectedAsset, useSelectedEvent } from '../../../lib/util';
import { GET_ALL_JOBS, GET_MO_DETAILS } from '../../../graph-queries/tar/job';
import { GET_CONFIG } from '../../../graph-queries/common/config';
import { GET_EVENTS_BY_ASSET } from '../../../graph-queries/tar/event';
import classes from './JobForm.module.scss';
import _ from 'lodash';
import {
  JOB_FIELD_WALK_STATUS_ENUM,
  JOB_SCHEDULE_STATUS_ENUM,
} from '../../../lib/constants/dbConstants';
import { renderOptions } from '../../../lib/util/render';

interface OptionType {
  value: number;
  label: string;
}

const JobForm: FC<{
  defaultValues?: TJob;
  onSubmit: (job: TJob) => Promise<unknown>;
  onClose: () => void;
  type: 'add' | 'edit';
  isLoading?: boolean;
}> = ({ defaultValues, onSubmit, type, onClose, isLoading }) => {
  const { event: eventFromParams } = useSelectedEvent();
  const defaultEventId = type === 'add' ? eventFromParams?.eventId : defaultValues?.eventId;
  const asset = useSelectedAsset();

  const { data: config } = useQuery(GET_CONFIG);
  const isRealTimeEnabled = !!config?.config.featureFlags.find(
    (ff) => ff.flag === 'FEATURE_FLAG_REALTIME_MO_API'
  )?.isEnabled;

  const { data: eventData, error: eventError } = useQuery(GET_EVENTS_BY_ASSET, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
  });

  const { data: allJobsData, error: allJobsError } = useQuery(GET_ALL_JOBS);

  const [fetchSapMo, { data: sapMo }] = useLazyQuery(GET_MO_DETAILS);
  const [moStatus, setMoStatus] = useState('');
  const [moDuplicate, setMoDuplicate] = useState('');
  const [jobDescription, setJobDescription] = useState('');

  const eventOptions: OptionsOrGroups<OptionType, GroupBase<OptionType>> | undefined =
    eventData?.eventsByAssetId.map((event) => ({
      value: event.eventId,
      label: event.eventDescription,
    }));

  const [selectedEvent, setSelectedEvent] = useState<SingleValue<OptionType>>(null);
  const handleEventChange = (option: SingleValue<OptionType>) => {
    setSelectedEvent(option);
  };

  // verify if MO number is duplicate
  const isMoDuplicate = (moNumber: string) => {
    if (!allJobsData || !moNumber || moNumber.trim() === '') {
      setMoDuplicate('');
      return;
    }

    // Excluding current job when in edit mode
    const allJobsMoNumbers = allJobsData?.allJobs
      .filter((job) => type === 'edit' ? job.jobId !== defaultValues?.jobId : true)
      .map((job) => job.moNumber);
    if (allJobsMoNumbers?.includes(moNumber)) {
      setMoDuplicate('error');
      return;
    }

    setMoDuplicate('');
  };

  const submitMoNumber = (moNumber: string) => {
    setMoStatus('');
    setMoDuplicate('');
    setJobDescription('');

    if (!moNumber || moNumber.trim() === '') return;

    fetchSapMo({ variables: { moNumber, isRealTime: isRealTimeEnabled } })
      .then((res) => {
        if (res.error) {
          setMoStatus('error');
        } else {
          setMoStatus(res.data?.sapMo.statusFioriDescription || '');
          setJobDescription(res.data?.sapMo.workOrderDescription || '');
        }
      })
      .catch(() => {
        setMoStatus('error');
        setJobDescription('');
      });

    if (type === 'edit') {
      if (moNumber === defaultValues?.moNumber) return;
    }
    isMoDuplicate(moNumber);
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    // Prevent default behavior
    event.preventDefault();

    const data = new FormData(event.target as HTMLFormElement);
    const inputData = Object.fromEntries(data) as Record<string, string>;

    const job: TJob = {
      ...EMPTY_JOB,
      ..._.omit(inputData, 'eventScope'),
      workpackRequired:
        inputData.workpackRequired === 'required'
          ? true
          : inputData.workpackRequired === 'notRequired'
            ? false
            : null,
      mocRequired:
        inputData.mocRequired === 'required'
          ? true
          : inputData.mocRequired === 'notRequired'
            ? false
            : null,
      materialsRequired:
        inputData.materialsRequired === 'required'
          ? true
          : inputData.materialsRequired === 'notRequired'
            ? false
            : null,
      longLeadMaterialsRequired:
        inputData.longLeadMaterialsRequired === 'required'
          ? true
          : inputData.longLeadMaterialsRequired === 'notRequired'
            ? false
            : null,
      specialistVendorsRequired:
        inputData.specialistVendorsRequired === 'required'
          ? true
          : inputData.specialistVendorsRequired === 'notRequired'
            ? false
            : null,
      specialistToolingRequired:
        inputData.specialistToolingRequired === 'required'
          ? true
          : inputData.specialistToolingRequired === 'notRequired'
            ? false
            : null,
      scaffoldRequired:
        inputData.scaffoldRequired === 'required'
          ? true
          : inputData.scaffoldRequired === 'notRequired'
            ? false
            : null,
      insulationRequired:
        inputData.insulationRequired === 'required'
          ? true
          : inputData.insulationRequired === 'notRequired'
            ? false
            : null,
      liftPlanRequired:
        inputData.liftPlanRequired === 'required'
          ? true
          : inputData.liftPlanRequired === 'notRequired'
            ? false
            : null,
      ropeAccessRequired:
        inputData.ropeAccessRequired === 'required'
          ? true
          : inputData.ropeAccessRequired === 'notRequired'
            ? false
            : null,
      isolationRequired:
        inputData.isolationRequired === 'required'
          ? true
          : inputData.isolationRequired === 'notRequired'
            ? false
            : null,
      riggingRequired:
        inputData.riggingRequired === 'required'
          ? true
          : inputData.riggingRequired === 'notRequired'
            ? false
            : null,
      heatTracingRequired:
        inputData.heatTracingRequired === 'required'
          ? true
          : inputData.heatTracingRequired === 'notRequired'
            ? false
            : null,
    };
    if (defaultValues?.jobId) job.jobId = defaultValues.jobId;
    if (selectedEvent?.value) job.eventId = selectedEvent.value;
    if (defaultValues?.notes) job.notes = defaultValues.notes;

    onSubmit(job)
      .then(onClose)
      .catch((e) => {
        console.error(e);
      });
  };

  useEffect(() => {
    if (eventData) {
      const defaultEvent = eventData.eventsByAssetId.find(
        (event) => event.eventId === defaultEventId
      );
      if (defaultEvent) {
        setSelectedEvent({ value: defaultEvent.eventId, label: defaultEvent.eventDescription });
      }
    }
  }, [eventData, defaultEventId]);

  return (
    <Form className="row g-3 px-5" onSubmit={handleSubmit}>
      <div className="col-md-6">
        <FormGroup>
          <Label for="mo-number">MO Number</Label>
          <Input
            valid={moStatus !== '' && moStatus !== 'error' && moDuplicate !== 'error'}
            invalid={moStatus === 'error' || moDuplicate === 'error'}
            type="text"
            name="moNumber"
            id="mo-number"
            required
            defaultValue={defaultValues?.moNumber || ''}
            onChange={(e) => {
              // Reset errors and job description
              setMoStatus('');
              setMoDuplicate('');
              setJobDescription('');
            }}
            onBlur={(e) => submitMoNumber(e.target.value)}
          />
          <FormFeedback className={classes.moNumberFormFeedback}>
            {moDuplicate === 'error' ? 'Duplicate MO number' : moStatus === 'error' ? 'MO not found' : ''}
          </FormFeedback>
        </FormGroup>
      </div>
      {type === 'add' ? (
        <div className="col-md-12">
          <FormGroup>
            <Label for="job-description">Job Description</Label>
            <Input
              title="This description is automatically added and cannot be edited here. You may edit this later on the job details page."
              readOnly
              type="text"
              name="jobDescription"
              id="job-description"
              value={jobDescription}
              placeholder="Auto populated using MO Number"
            />
          </FormGroup>
        </div>
      ) : (
        <>
          <div className="col-md-6">
            <FormGroup>
              <Label for="event-scope">Event Scope</Label>
              <Select
                id="event-scope"
                name="eventScope"
                value={selectedEvent}
                options={eventOptions}
                isMulti={false}
                onChange={handleEventChange}
                required
                components={{
                  ...ReactSelectOverrides.default,
                  IndicatorSeparator: ReactSelectOverrides.NoIndicatorSeparator,
                }}
                placeholder="Select event"
                classNamePrefix="react-select"
              />
            </FormGroup>
          </div>
          <div className="col-md-12">
            <FormGroup>
              <Label for="job-description">Job Description</Label>
              <Input
                type="text"
                name="jobDescription"
                id="job-description"
                defaultValue={defaultValues?.jobDescription || ''}
              />
            </FormGroup>
          </div>
          <div className="col-md-12">
            <FormGroup>
              <Label for="assigned-planner">Assigned Planner</Label>
              <Input
                type="text"
                name="assignedPlanner"
                id="assigned-planner"
                defaultValue={defaultValues?.assignedPlanner || ''}
              />
            </FormGroup>
          </div>
          <div className="col-md-12">
            <Label className={classes.title}>Engineering Requirements</Label>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center">
                <Label className="x5-me-6">Workpack</Label>
                <div className="d-flex">
                  <FormGroup check className="me-3" inline>
                    <Input
                      id="workpack-required-1"
                      name="workpackRequired"
                      type="radio"
                      value="required"
                      defaultChecked={defaultValues?.workpackRequired === true}
                    />
                    <Label check for="workpack-required-1" className="ms-1">
                      Yes
                    </Label>
                  </FormGroup>
                  <FormGroup check className="me-0" inline>
                    <Input
                      id="workpack-required-2"
                      name="workpackRequired"
                      type="radio"
                      value="notRequired"
                      defaultChecked={defaultValues?.workpackRequired === false}
                    />
                    <Label check for="workpack-required-2" className="ms-1">
                      No
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </FormGroup>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center">
                <Label className="mb-0">MOC</Label>
                <div className="d-flex">
                  <FormGroup check inline className="me-3">
                    <Input
                      id="moc-required-1"
                      name="mocRequired"
                      type="radio"
                      value="required"
                      defaultChecked={defaultValues?.mocRequired === true}
                    />
                    <Label check htmlFor="moc-required-1" className="ms-1">
                      Yes
                    </Label>
                  </FormGroup>
                  <FormGroup check inline className="me-0">
                    <Input
                      id="moc-required-2"
                      name="mocRequired"
                      type="radio"
                      value="notRequired"
                      defaultChecked={defaultValues?.mocRequired === false}
                    />
                    <Label check htmlFor="moc-required-2" className="ms-1">
                      No
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </FormGroup>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center">
                <Label className="mb-0">Long Lead Time Materials</Label>
                <div className="d-flex">
                  <FormGroup check inline className="me-3">
                    <Input
                      id="long-lead-materials-required-1"
                      name="longLeadMaterialsRequired"
                      type="radio"
                      value="required"
                      defaultChecked={defaultValues?.longLeadMaterialsRequired === true}
                    />
                    <Label check htmlFor="long-lead-materials-required-1" className="ms-1">
                      Yes
                    </Label>
                  </FormGroup>
                  <FormGroup check inline className="me-0">
                    <Input
                      id="long-lead-materials-required-2"
                      name="longLeadMaterialsRequired"
                      type="radio"
                      value="notRequired"
                      defaultChecked={defaultValues?.longLeadMaterialsRequired === false}
                    />
                    <Label check htmlFor="long-lead-materials-required-2" className="ms-1">
                      No
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </FormGroup>
            <Label className={classes.title}>Support Services Requirements</Label>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center">
                <Label className="mb-0">Scaffold</Label>
                <div className="d-flex">
                  <FormGroup check inline className="me-3">
                    <Input
                      id="scaffold-required-1"
                      name="scaffoldRequired"
                      type="radio"
                      value="required"
                      defaultChecked={defaultValues?.scaffoldRequired === true}
                    />
                    <Label check htmlFor="scaffold-required-1" className="ms-1">
                      Yes
                    </Label>
                  </FormGroup>
                  <FormGroup check inline className="me-0">
                    <Input
                      id="scaffold-required-2"
                      name="scaffoldRequired"
                      type="radio"
                      value="notRequired"
                      defaultChecked={defaultValues?.scaffoldRequired === false}
                    />
                    <Label check htmlFor="scaffold-required-2" className="ms-1">
                      No
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </FormGroup>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center">
                <Label className="mb-0">Insulation</Label>
                <div className="d-flex">
                  <FormGroup check inline className="me-3">
                    <Input
                      id="insulation-required-1"
                      name="insulationRequired"
                      type="radio"
                      value="required"
                      defaultChecked={defaultValues?.insulationRequired === true}
                    />
                    <Label check htmlFor="insulation-required-1" className="ms-1">
                      Yes
                    </Label>
                  </FormGroup>
                  <FormGroup check inline className="me-0">
                    <Input
                      id="insulation-required-2"
                      name="insulationRequired"
                      type="radio"
                      value="notRequired"
                      defaultChecked={defaultValues?.insulationRequired === false}
                    />
                    <Label check htmlFor="insulation-required-2" className="ms-1">
                      No
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </FormGroup>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center">
                <Label className="mb-0">Lift Plan</Label>
                <div className="d-flex">
                  <FormGroup check inline className="me-3">
                    <Input
                      id="lift-plan-required-1"
                      name="liftPlanRequired"
                      type="radio"
                      value="required"
                      defaultChecked={defaultValues?.liftPlanRequired === true}
                    />
                    <Label check htmlFor="lift-plan-required-1" className="ms-1">
                      Yes
                    </Label>
                  </FormGroup>
                  <FormGroup check inline className="me-0">
                    <Input
                      id="lift-plan-required-2"
                      name="liftPlanRequired"
                      type="radio"
                      value="notRequired"
                      defaultChecked={defaultValues?.liftPlanRequired === false}
                    />
                    <Label check htmlFor="lift-plan-required-2" className="ms-1">
                      No
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </FormGroup>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center">
                <Label className="mb-0">Rope Access</Label>
                <div className="d-flex">
                  <FormGroup check inline className="me-3">
                    <Input
                      id="rope-access-required-1"
                      name="ropeAccessRequired"
                      type="radio"
                      value="required"
                      defaultChecked={defaultValues?.ropeAccessRequired === true}
                    />
                    <Label check htmlFor="rope-access-required-1" className="ms-1">
                      Yes
                    </Label>
                  </FormGroup>
                  <FormGroup check inline className="me-0">
                    <Input
                      id="rope-access-required-2"
                      name="ropeAccessRequired"
                      type="radio"
                      value="notRequired"
                      defaultChecked={defaultValues?.ropeAccessRequired === false}
                    />
                    <Label check htmlFor="rope-access-required-2" className="ms-1">
                      No
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </FormGroup>
            <Label className={classes.title}>Purchase Order Requirements</Label>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center">
                <Label className="mb-0">Materials</Label>
                <div className="d-flex">
                  <FormGroup check inline className="me-3">
                    <Input
                      id="materials-required-1"
                      name="materialsRequired"
                      type="radio"
                      value="required"
                      defaultChecked={defaultValues?.materialsRequired === true}
                    />
                    <Label check htmlFor="materials-required-1" className="ms-1">
                      Yes
                    </Label>
                  </FormGroup>
                  <FormGroup check inline className="me-0">
                    <Input
                      id="materials-required-2"
                      name="materialsRequired"
                      type="radio"
                      value="notRequired"
                      defaultChecked={defaultValues?.materialsRequired === false}
                    />
                    <Label check htmlFor="materials-required-2" className="ms-1">
                      No
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </FormGroup>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center">
                <Label className="mb-0">Specialist Vendors</Label>
                <div className="d-flex">
                  <FormGroup check inline className="me-3">
                    <Input
                      id="specialist-vendors-required-1"
                      name="specialistVendorsRequired"
                      type="radio"
                      value="required"
                      defaultChecked={defaultValues?.specialistVendorsRequired === true}
                    />
                    <Label check htmlFor="specialist-vendors-required-1" className="ms-1">
                      Yes
                    </Label>
                  </FormGroup>
                  <FormGroup check inline className="me-0">
                    <Input
                      id="specialist-vendors-required-2"
                      name="specialistVendorsRequired"
                      type="radio"
                      value="notRequired"
                      defaultChecked={defaultValues?.specialistVendorsRequired === false}
                    />
                    <Label check htmlFor="specialist-vendors-required-2" className="ms-1">
                      No
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </FormGroup>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center">
                <Label className="mb-0">Specialist Tooling</Label>
                <div className="d-flex">
                  <FormGroup check inline className="me-3">
                    <Input
                      id="specialist-tooling-required-1"
                      name="specialistToolingRequired"
                      type="radio"
                      value="required"
                      defaultChecked={defaultValues?.specialistToolingRequired === true}
                    />
                    <Label check htmlFor="specialist-tooling-required-1" className="ms-1">
                      Yes
                    </Label>
                  </FormGroup>
                  <FormGroup check inline className="me-0">
                    <Input
                      id="specialist-tooling-required-2"
                      name="specialistToolingRequired"
                      type="radio"
                      value="notRequired"
                      defaultChecked={defaultValues?.specialistToolingRequired === false}
                    />
                    <Label check htmlFor="specialist-tooling-required-2" className="ms-1">
                      No
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </FormGroup>
            <Label className={classes.title}>Operational Requirements</Label>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center">
                <Label className="mb-0">Isolation</Label>
                <div className="d-flex">
                  <FormGroup check inline className="me-3">
                    <Input
                      id="isolation-required-1"
                      name="isolationRequired"
                      type="radio"
                      value="required"
                      defaultChecked={defaultValues?.isolationRequired === true}
                    />
                    <Label check htmlFor="isolation-required-1" className="ms-1">
                      Yes
                    </Label>
                  </FormGroup>
                  <FormGroup check inline className="me-0">
                    <Input
                      id="isolation-required-2"
                      name="isolationRequired"
                      type="radio"
                      value="notRequired"
                      defaultChecked={defaultValues?.isolationRequired === false}
                    />
                    <Label check htmlFor="isolation-required-2" className="ms-1">
                      No
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </FormGroup>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center">
                <Label className="mb-0">Rigging</Label>
                <div className="d-flex">
                  <FormGroup check inline className="me-3">
                    <Input
                      id="rigging-required-1"
                      name="riggingRequired"
                      type="radio"
                      value="required"
                      defaultChecked={defaultValues?.riggingRequired === true}
                    />
                    <Label check htmlFor="rigging-required-1" className="ms-1">
                      Yes
                    </Label>
                  </FormGroup>
                  <FormGroup check inline className="me-0">
                    <Input
                      id="rigging-required-2"
                      name="riggingRequired"
                      type="radio"
                      value="notRequired"
                      defaultChecked={defaultValues?.riggingRequired === false}
                    />
                    <Label check htmlFor="rigging-required-2" className="ms-1">
                      No
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </FormGroup>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center">
                <Label className="mb-0">Heat Tracing</Label>
                <div className="d-flex">
                  <FormGroup check inline className="me-3">
                    <Input
                      id="heat-tracing-required-1"
                      name="heatTracingRequired"
                      type="radio"
                      value="required"
                      defaultChecked={defaultValues?.heatTracingRequired === true}
                    />
                    <Label check htmlFor="heat-tracing-required-1" className="ms-1">
                      Yes
                    </Label>
                  </FormGroup>
                  <FormGroup check inline className="me-0">
                    <Input
                      id="heat-tracing-required-2"
                      name="heatTracingRequired"
                      type="radio"
                      value="notRequired"
                      defaultChecked={defaultValues?.heatTracingRequired === false}
                    />
                    <Label check htmlFor="heat-tracing-required-2" className="ms-1">
                      No
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </FormGroup>
          </div>
          <div className="col-md-12">
            <FormGroup>
              <Label for="schedule-status">Schedule Status</Label>
              <Input
                type="select"
                name="scheduleStatus"
                id="schedule-status"
                className="form-select-placeholder-hack"
                required
                role="combobox"
                defaultValue={defaultValues?.scheduleStatus || ''}
              >
                <option disabled hidden value="" key="_placeholder_">
                  Choose an option
                </option>
                {renderOptions(JOB_SCHEDULE_STATUS_ENUM)}
              </Input>
            </FormGroup>
          </div>
          <div className="col-md-12">
            <FormGroup>
              <Label for="field-walk">Field Walk</Label>
              <Input
                type="select"
                name="fieldWalk"
                id="field-walk"
                className="form-select-placeholder-hack"
                required
                role="combobox"
                defaultValue={defaultValues?.fieldWalk || ''}
              >
                <option disabled hidden value="" key="_placeholder_">
                  Choose an option
                </option>
                {renderOptions(JOB_FIELD_WALK_STATUS_ENUM)}
              </Input>
            </FormGroup>
          </div>
        </>
      )}
      {type === 'add' ? (
        <div className="col-md-12">
          <Button
            type="submit"
            size="lg"
            className="w-100 my-4"
            theme="darker"
            level="primary"
            disabled={isLoading || moDuplicate === 'error' || moStatus === 'error'}
          >
            Add new job
          </Button>
        </div>
      ) : (
        <>
          <div className="col-md-6">
            <Button
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="tertiary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
          <div className="col-md-6">
            <Button
              type="submit"
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="primary"
              disabled={isLoading || moDuplicate === 'error' || moStatus === 'error'}
            >
              Save changes
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};

export default JobForm;
